import { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * Generates responsive srcset for Unsplash images
 * Unsplash supports: ?w=400&fm=webp, ?w=800&fm=webp, etc.
 */
function getResponsiveSrc(src) {
  if (!src) return { src, srcSet: '', sizes: '' };

  const isUnsplash = src.includes('images.unsplash.com');
  const isPexels = src.includes('images.pexels.com');
  const isStatic = src.includes('static.prod-images.emergentagent.com');

  if (isUnsplash) {
    const base = src.split('?')[0];
    const srcSet = [
      `${base}?w=400&auto=format&q=75 400w`,
      `${base}?w=800&auto=format&q=75 800w`,
      `${base}?w=1200&auto=format&q=80 1200w`,
      `${base}?w=1920&auto=format&q=80 1920w`,
    ].join(', ');
    const webpSrcSet = [
      `${base}?w=400&fm=webp&q=75 400w`,
      `${base}?w=800&fm=webp&q=75 800w`,
      `${base}?w=1200&fm=webp&q=80 1200w`,
      `${base}?w=1920&fm=webp&q=80 1920w`,
    ].join(', ');
    return { src: `${base}?w=1200&auto=format&q=80`, srcSet, webpSrcSet, sizes: '(max-width: 640px) 400px, (max-width: 1024px) 800px, (max-width: 1536px) 1200px, 1920px' };
  }

  if (isPexels) {
    const base = src.split('?')[0];
    const srcSet = [
      `${base}?auto=compress&cs=tinysrgb&w=400 400w`,
      `${base}?auto=compress&cs=tinysrgb&w=800 800w`,
      `${base}?auto=compress&cs=tinysrgb&w=1200 1200w`,
    ].join(', ');
    return { src: `${base}?auto=compress&cs=tinysrgb&w=1200`, srcSet, webpSrcSet: '', sizes: '(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px' };
  }

  // Static images and others: no srcset optimization
  return { src, srcSet: '', webpSrcSet: '', sizes: '' };
}

export default function OptimizedImage({ src, alt, className = '', aspectClass = 'aspect-[4/3]' }) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef(null);
  const { src: optimizedSrc, srcSet, webpSrcSet, sizes } = getResponsiveSrc(src);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: '300px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={`relative overflow-hidden bg-[#0A120E] ${aspectClass} ${className}`}>
      {/* Shimmer placeholder */}
      <div className={`absolute inset-0 bg-gradient-to-r from-[#0A120E] via-[#111C16] to-[#0A120E] transition-opacity duration-700 ${loaded ? 'opacity-0' : 'opacity-100 animate-pulse'}`} />
      {inView && (
        <picture>
          {webpSrcSet && <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />}
          {srcSet && <source srcSet={srcSet} sizes={sizes} />}
          <motion.img
            src={optimizedSrc}
            alt={alt}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={loaded ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="w-full h-full object-cover"
          />
        </picture>
      )}
    </div>
  );
}
